import {ModalState} from "../../../types/modals";
import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {api} from "../../api";
import {toastSuccess} from "../admin";
import {changeGameDetail} from "../adminGameView";
import {UPLOAD_COMPLETED, UPLOAD_FAILED, UPLOAD_IN_PROGRESS, UPLOAD_PENDING} from "../../../settings";

export type SectionPdfModalState = ModalState & {
    section_id: string
    isProcessing: boolean;
    processingStatus: string | null;
    currentPage: number | null;
    totalPages: number | null;
}

interface ProcessingStatus {
    status: string;
    current_page?: number;
    total_pages?: number;
    version_sections?: any;
    success_message?: string;
    error_message?: string;
}

const initialState: SectionPdfModalState = {
    loading: false,
    isOpen: false,
    section_id: '',
    error: null,
    isProcessing: false,
    processingStatus: null,
    currentPage: null,
    totalPages: null
}

type ProcessingStatusPayload = {
    status: string | null;
    currentPage?: number | null;
    totalPages?: number | null;
}

type SectionPdfModalPayload = {
    section_id: string
}

export const saveSectionPdf = createAsyncThunk(
    'sectionPdfModal/saveSectionPdf',
    async (payload: FormData, thunkAPI) => {
        const section_id = payload.get('section_id');
        const game_id = payload.get('game_id');
        const version_id = payload.get('version_id');
        const dispatch = thunkAPI.dispatch;
        if (section_id === null) {
            return thunkAPI.rejectWithValue('Section id is missing.');
        }
        try {
            const response = await api.post(`/API/admin/games/${game_id}/versions/${version_id}/sections/${section_id}/pdf/`, payload, true);

            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }

            if (response.status_id) {
                dispatch(setProcessingStatus({
                    status: UPLOAD_PENDING,
                    currentPage: null,
                    totalPages: null,
                }));
                const pollStatus = async (): Promise<ProcessingStatus> => {
                    const statusResponse: ProcessingStatus = await api.get(`/API/admin/pdf-processing-status/${response.status_id}/`);
                    dispatch(setProcessingStatus({
                        status: statusResponse.status,
                        currentPage: statusResponse.current_page ?? null,
                        totalPages: statusResponse.total_pages ?? null
                    }));
                    if (statusResponse.status === UPLOAD_COMPLETED) {
                        if (statusResponse.version_sections) {
                            dispatch(changeGameDetail({version_sections: statusResponse.version_sections}));
                        }
                        if (statusResponse.success_message) {
                            dispatch(toastSuccess(statusResponse.success_message));
                        }
                        dispatch(setProcessingStatus({
                            status: null,
                            currentPage: null,
                            totalPages: null
                        }));
                        return statusResponse;
                    } else if (statusResponse.status === UPLOAD_FAILED) {
                        dispatch(setProcessingStatus({
                            status: null,
                            currentPage: null,
                            totalPages: null
                        }));
                        throw new Error(statusResponse.error_message);
                    }

                    await new Promise(resolve => setTimeout(resolve, 1000));
                    return pollStatus();
                };

                return await pollStatus();
            }

            if (response.version_sections) {
                dispatch(changeGameDetail({version_sections: response.version_sections}));
            }
            return response;
        } catch (error: any) {
            dispatch(setProcessingStatus({
                status: null,
                currentPage: null,
                totalPages: null
            }));
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

const slice = createSlice({
    name: 'sectionPdfModal',
    initialState,
    reducers: {
        openModal: (state, action: PayloadAction<SectionPdfModalPayload>) => {
            state.isOpen = true;
            state.section_id = action.payload.section_id;
        },
        closeModal: () => {
            return initialState;
        },
        setProcessingStatus: (state, action: PayloadAction<ProcessingStatusPayload>) => {
            state.processingStatus = action.payload.status;
            if (action.payload.currentPage !== undefined) {
                state.currentPage = action.payload.currentPage;
            }
            if (action.payload.totalPages !== undefined) {
                state.totalPages = action.payload.totalPages;
            }
            state.isProcessing = action.payload.status === UPLOAD_PENDING || action.payload.status === UPLOAD_IN_PROGRESS;
        },
    },
    extraReducers: builder => {
        builder.addCase(saveSectionPdf.pending, (state) => {
            // state.loading = true;
        });
        builder.addCase(saveSectionPdf.fulfilled, (state) => {
            return initialState;
        });
        builder.addCase(saveSectionPdf.rejected, (state, action) => {
            state.loading = false;
            state.currentPage = null;
            state.totalPages = null;
            if (typeof action.payload === 'string') {
                state.error = action.payload as string;
            } else if (typeof action.error?.message === 'string') {
                state.error = action.error.message;
            }
        });
    }
});


export const {
    openModal,
    closeModal,
    setProcessingStatus
} = slice.actions;

export default slice.reducer;